import React, { useEffect, useRef } from "react";
import { useSetRecoilState } from "recoil";
import styled from "styled-components";
import { Entry } from "contentful";

import { Text, breakpoints } from "@trailhead/asurion-ui";
import { componentRefListState } from "@trailhead/global-device-context";
import { PageSection } from "@trailhead/contentful";

import CustomContentCard from "../customCards";
import { SectionType } from "../../types";
import { CardBannerLg } from "./CardBannerLg";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding-top: 1.5rem;
    gap: 1.5rem;
    margin: auto;

    ${breakpoints.md2} {
        padding-top: 3.75rem;
        gap: 3.25rem;
    }
`;

const GridOverflowWrapper = styled.div`
    box-sizing: border-box;
    overflow-x: auto;
    max-width: 100%;
`;

const GridContainer = styled.div<{ itemsPerRow: number }>`
    box-sizing: border-box;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: auto;
    align-items: stretch;
    width: inherit;
    padding: 0 1rem;
    box-sizing: content-box;
    column-gap: 0.75rem;
    justify-content: center;
    min-width: min-content;
    max-width: 1200px;

    ${breakpoints.md} {
        padding: 0 1.5rem;
    }

    ${breakpoints.md2} {
        gap: 1.5rem;
        grid-auto-flow: row;
        grid-template-columns: repeat(${(props) => props.itemsPerRow}, auto);
    }
`;

const SectionHeader = styled(Text)`
    text-align: center;
    padding: 0 1rem;
`;

type GridProps = SectionType & {
    itemsPerRow: number;
}

const GridCenter = (props: GridProps): JSX.Element => {
    const { entryName, heading, cards, itemsPerRow } = props;

    const containerRef = useRef(null);

    const setComponentRefList = useSetRecoilState(componentRefListState);

    useEffect(() => {
        setComponentRefList((oldList) => ({
            ...oldList,
            [entryName]: containerRef.current,
        }));
        return () => {
            setComponentRefList((oldList) => {
                delete oldList[entryName];
                return {
                    ...oldList,
                };
            });
        }
    }, [entryName, setComponentRefList]);

    return (
        <Container id={entryName} ref={containerRef}>
            <SectionHeader
                weight="base"
                size={6}
                dangerouslySetInnerHTML={{ __html: heading || "" }}
            />
            <GridOverflowWrapper>
                <GridContainer itemsPerRow={itemsPerRow}>
                    {!!cards && cards.map((card) => (<CustomContentCard {...card} />))}
                </GridContainer>
            </GridOverflowWrapper>
        </Container>
    );
}

// TODO: Refactor types here to reflect actual Page types

export const CustomSection = (props: SectionType): JSX.Element => {
    const { sectionType } = props;

    // TODO: Apply validation for data passed to components here. Fail loudly.
    switch (sectionType) {
        case "grid-center--4":
            return <GridCenter itemsPerRow={4} {...props} />;
        case "grid-center--3":
            return <GridCenter itemsPerRow={3} {...props} />;
        case "grid-center--2":
            return <GridCenter itemsPerRow={2} {...props} />;
        case "card-banner--lg":
            return <CardBannerLg {...props} />;
        default:
            return <GridCenter itemsPerRow={3} {...props} />
    }

}

// NOTE: For testing only, will use one above once refactored
export const CustomSectionDemo = (props: Entry<PageSection>): JSX.Element => {
    const { fields: { entryName } } = props;

    return (<p style={{ height: "500px" }}>
        {entryName}
    </p>)
}